import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  HardDrive,
  Folder,
  FolderOpen,
  File,
  FileText,
  Image,
  Video,
  Upload,
  Download,
  Trash2,
  RefreshCw,
  Search,
  Plus,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  ExternalLink,
  Eye,
  Edit,
  Share2,
  ArrowLeft,
  FolderPlus,
  Grid,
  List,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const API = process.env.REACT_APP_BACKEND_URL;

// Folder Structure Template
const FOLDER_STRUCTURE = {
  name: 'Recruitment',
  children: [
    {
      name: 'Candidates',
      description: 'Candidate files organized by name',
      template: {
        name: '{{candidate_name}}',
        children: [
          { name: 'CV', description: 'Resume and CV files' },
          { name: 'Videos', description: 'Introduction and assessment videos' },
          { name: 'Certificates', description: 'Education and certification documents' },
          { name: 'Assessment', description: 'Assessment results and submissions' },
          { name: 'Offer Letter', description: 'Offer letters and negotiations' },
          { name: 'Agreement', description: 'Signed agreements and contracts' }
        ]
      }
    },
    {
      name: 'Jobs',
      description: 'Job postings and related documents'
    },
    {
      name: 'Templates',
      description: 'Document templates'
    },
    {
      name: 'Reports',
      description: 'Generated reports and exports'
    }
  ]
};

// File type icons
const getFileIcon = (mimeType, name) => {
  if (mimeType?.includes('folder') || mimeType === 'application/vnd.google-apps.folder') {
    return Folder;
  }
  if (mimeType?.includes('image')) return Image;
  if (mimeType?.includes('video')) return Video;
  if (mimeType?.includes('pdf') || name?.endsWith('.pdf')) return FileText;
  return File;
};

const getFileColor = (mimeType) => {
  if (mimeType?.includes('folder')) return 'text-yellow-500';
  if (mimeType?.includes('image')) return 'text-purple-500';
  if (mimeType?.includes('video')) return 'text-red-500';
  if (mimeType?.includes('pdf')) return 'text-red-600';
  return 'text-blue-500';
};

const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

const GoogleDriveIntegration = ({ onBack, candidateId, candidateName }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [setupProgress, setSetupProgress] = useState(0);

  useEffect(() => {
    checkConnection();
  }, []);

  useEffect(() => {
    if (connected) {
      fetchFiles();
    }
  }, [connected, currentFolder]);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/google_drive/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      if (response.data.root_folder_id) {
        setCurrentFolder(response.data.root_folder_id);
      }
    } catch (error) {
      console.error('Error checking Drive connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/google_drive/files`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          folder_id: currentFolder,
          search: searchQuery
        }
      });
      setFiles(response.data.files || []);
    } catch (error) {
      console.error('Error fetching files:', error);
      // Show demo data
      setFiles(getDemoFiles());
    }
  };

  const getDemoFiles = () => [
    {
      id: 'folder1',
      name: 'Candidates',
      mimeType: 'application/vnd.google-apps.folder',
      modifiedTime: new Date().toISOString(),
      owners: [{ displayName: 'Admin' }]
    },
    {
      id: 'folder2',
      name: 'Jobs',
      mimeType: 'application/vnd.google-apps.folder',
      modifiedTime: new Date(Date.now() - 86400000).toISOString(),
      owners: [{ displayName: 'Admin' }]
    },
    {
      id: 'folder3',
      name: 'Templates',
      mimeType: 'application/vnd.google-apps.folder',
      modifiedTime: new Date(Date.now() - 172800000).toISOString(),
      owners: [{ displayName: 'Admin' }]
    },
    {
      id: 'file1',
      name: 'Recruitment_Report_Q2.pdf',
      mimeType: 'application/pdf',
      size: '2457600',
      modifiedTime: new Date(Date.now() - 3600000).toISOString(),
      owners: [{ displayName: 'HR Team' }],
      webViewLink: '#'
    },
    {
      id: 'file2',
      name: 'Interview_Guidelines.docx',
      mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      size: '156000',
      modifiedTime: new Date(Date.now() - 7200000).toISOString(),
      owners: [{ displayName: 'Admin' }],
      webViewLink: '#'
    }
  ];

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/google_drive/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.oauth_url) {
        window.location.href = response.data.oauth_url;
      } else {
        toast.info(response.data.message || 'Drive connection initiated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Google Drive');
    }
  };

  const handleSetupFolders = async () => {
    setShowSetup(true);
    setSetupProgress(0);
    
    try {
      const token = localStorage.getItem('token');
      
      // Simulate folder creation progress
      for (let i = 1; i <= 5; i++) {
        await new Promise(resolve => setTimeout(resolve, 500));
        setSetupProgress(i * 20);
      }
      
      await axios.post(`${API}/api/integrations/google_drive/setup-folders`, {
        structure: FOLDER_STRUCTURE
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSetupProgress(100);
      toast.success('Folder structure created successfully');
      await fetchFiles();
    } catch (error) {
      toast.error('Failed to create folder structure');
    } finally {
      setTimeout(() => setShowSetup(false), 1000);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder_id', currentFolder || '');
      if (candidateId) formData.append('candidate_id', candidateId);

      await axios.post(`${API}/api/integrations/google_drive/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('File uploaded to Google Drive');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/google_drive/folders`, {
        name: newFolderName,
        parent_id: currentFolder
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Folder created');
      setNewFolderName('');
      setShowNewFolder(false);
      fetchFiles();
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const handleDelete = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/integrations/google_drive/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const handleOpenFolder = (folder) => {
    setFolderPath([...folderPath, { id: currentFolder, name: folder.name }]);
    setCurrentFolder(folder.id);
  };

  const handleNavigateBack = (index) => {
    if (index === -1) {
      setFolderPath([]);
      setCurrentFolder(null);
    } else {
      const newPath = folderPath.slice(0, index + 1);
      setFolderPath(newPath);
      setCurrentFolder(newPath[index]?.id || null);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/google_drive/sync`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchFiles();
      toast.success('Files synced');
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openInDrive = (file) => {
    if (file.webViewLink) {
      window.open(file.webViewLink, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to App Hub
        </Button>
        
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <HardDrive className="h-8 w-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Connect Google Drive</h2>
            <p className="text-slate-500 mb-6">
              Store candidate documents, resumes, and files directly in Google Drive with automatic folder organization.
            </p>
            <Button onClick={handleConnect} className="bg-yellow-500 hover:bg-yellow-600">
              <HardDrive className="h-4 w-4 mr-2" />
              Connect Google Drive
            </Button>
            <div className="mt-6 p-4 bg-slate-50 rounded-lg text-left">
              <h4 className="font-medium text-slate-700 mb-2">Auto-created folder structure:</h4>
              <pre className="text-xs text-slate-500">
{`Recruitment/
├── Candidates/
│   └── [Candidate Name]/
│       ├── CV/
│       ├── Videos/
│       ├── Certificates/
│       ├── Assessment/
│       ├── Offer Letter/
│       └── Agreement/
├── Jobs/
├── Templates/
└── Reports/`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="google-drive-integration">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
            <HardDrive className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-800">Google Drive</h1>
            <p className="text-xs text-slate-500">{files.length} items</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}>
                  {viewMode === 'grid' ? <List className="h-4 w-4" /> : <Grid className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent>{viewMode === 'grid' ? 'List View' : 'Grid View'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="icon" onClick={handleSync} disabled={syncing}>
                  <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Sync</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button variant="outline" onClick={() => setShowNewFolder(true)}>
            <FolderPlus className="h-4 w-4 mr-2" />
            New Folder
          </Button>
          <label>
            <Button as="span" disabled={uploading}>
              {uploading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload
            </Button>
            <input type="file" className="hidden" onChange={handleUpload} />
          </label>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="px-4 py-2 border-b bg-slate-50 flex items-center gap-1 text-sm">
        <button
          onClick={() => handleNavigateBack(-1)}
          className="text-blue-600 hover:underline"
        >
          My Drive
        </button>
        {folderPath.map((folder, idx) => (
          <React.Fragment key={idx}>
            <ChevronRight className="h-4 w-4 text-slate-400" />
            <button
              onClick={() => handleNavigateBack(idx)}
              className={idx === folderPath.length - 1 ? 'text-slate-800' : 'text-blue-600 hover:underline'}
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Search */}
      <div className="p-4 border-b">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* File Grid/List */}
      <div className="flex-1 overflow-y-auto p-4">
        {files.length === 0 ? (
          <div className="text-center py-12">
            <Folder className="h-16 w-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">This folder is empty</h3>
            <p className="text-slate-500 mb-4">Upload files or create folders to get started</p>
            <Button variant="outline" onClick={handleSetupFolders}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Setup Recruitment Folders
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {files.map(file => {
              const FileIcon = getFileIcon(file.mimeType, file.name);
              const isFolder = file.mimeType?.includes('folder');
              return (
                <div
                  key={file.id}
                  className="group relative p-4 rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                  onClick={() => isFolder ? handleOpenFolder(file) : setSelectedFile(file)}
                  onDoubleClick={() => !isFolder && openInDrive(file)}
                >
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => e.stopPropagation()}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {!isFolder && (
                          <DropdownMenuItem onClick={() => openInDrive(file)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open in Drive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem>
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(file.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex flex-col items-center">
                    <FileIcon className={`h-12 w-12 mb-3 ${getFileColor(file.mimeType)}`} />
                    <p className="text-sm font-medium text-slate-700 text-center truncate w-full">{file.name}</p>
                    {!isFolder && (
                      <p className="text-xs text-slate-500 mt-1">{formatFileSize(file.size)}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-1">
            {files.map(file => {
              const FileIcon = getFileIcon(file.mimeType, file.name);
              const isFolder = file.mimeType?.includes('folder');
              return (
                <div
                  key={file.id}
                  className="group flex items-center gap-4 p-3 rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => isFolder ? handleOpenFolder(file) : setSelectedFile(file)}
                >
                  <FileIcon className={`h-6 w-6 ${getFileColor(file.mimeType)}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-700 truncate">{file.name}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-6 text-sm text-slate-500">
                    <span className="w-24">{file.owners?.[0]?.displayName || '-'}</span>
                    <span className="w-24">{new Date(file.modifiedTime).toLocaleDateString()}</span>
                    <span className="w-20 text-right">{!isFolder ? formatFileSize(file.size) : '-'}</span>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {!isFolder && (
                          <DropdownMenuItem onClick={() => openInDrive(file)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open in Drive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem>
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(file.id)}>
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolder} onOpenChange={setShowNewFolder}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Folder</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Folder name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewFolder(false)}>Cancel</Button>
            <Button onClick={handleCreateFolder}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Setup Progress Dialog */}
      <Dialog open={showSetup} onOpenChange={() => {}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Setting up folder structure...</DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="w-full bg-slate-200 rounded-full h-2 mb-4">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${setupProgress}%` }}
              />
            </div>
            <p className="text-center text-slate-600">
              {setupProgress < 100 ? 'Creating folders...' : 'Complete!'}
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Zero Storage Notice */}
      <div className="p-3 border-t bg-blue-50 text-sm text-blue-700 flex items-center gap-2">
        <AlertCircle className="h-4 w-4" />
        <span>Files are stored in Google Drive only. CRM stores metadata (file IDs, URLs) - not actual files.</span>
      </div>
    </div>
  );
};

export default GoogleDriveIntegration;
